/**
 * FitTrackPage - Main page for the Fitness Tracker feature
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFitnessStore } from '../stores/fitnessStore';
import { useCoachStore } from '../stores/coachStore';
import FitTrackHeader from '../components/fitness/FitTrackHeader';
import WorkoutCalendar from '../components/fitness/WorkoutCalendar';
import WorkoutList from '../components/fitness/WorkoutList';
import AddWorkoutModal from '../components/fitness/AddWorkoutModal';
import ScheduleWorkoutModal from '../components/fitness/ScheduleWorkoutModal';
import ConnectSheetsModal from '../components/fitness/ConnectSheetsModal';
import SchemaMapperModal from '../components/fitness/SchemaMapperModal';
import DayDetailModal from '../components/fitness/DayDetailModal';
import ExerciseSearchModal from '../components/fitness/ExerciseSearchModal';
import LiveWorkoutModal from '../components/fitness/LiveWorkoutModal';
import CoachNotesSection from '../components/fitness/CoachNotesSection';
import AddNoteModal from '../components/coach/AddNoteModal';

const FitTrackPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('client');
  const dateFromUrl = searchParams.get('date');

  const {
    isLoadingWorkouts,
    initialize,
    viewMode,
    selectedDay,
    setSelectedDay,
    isAddWorkoutModalOpen,
    closeAddWorkoutModal,
    isScheduleWorkoutModalOpen,
    closeScheduleWorkoutModal,
    isConnectSheetsModalOpen,
    closeConnectSheetsModal,
    isSchemaMapperOpen,
    closeSchemaMapper,
    isLiveWorkoutModalOpen,
    closeLiveWorkoutModal,
  } = useFitnessStore();

  const {
    initialize: initializeCoach,
    viewingClientId,
    isAddNoteModalOpen,
    closeAddNoteModal,
  } = useCoachStore();

  // Sync date from URL on mount
  useEffect(() => {
    if (dateFromUrl && /^\d{4}-\d{2}-\d{2}$/.test(dateFromUrl)) {
      setSelectedDay(dateFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL when selectedDay changes
  useEffect(() => {
    const currentDate = searchParams.get('date');
    if (selectedDay && selectedDay !== currentDate) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('date', selectedDay);
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedDay, searchParams, setSearchParams]);

  // Initialize stores once on mount
  // Note: initialize() now handles client changes internally via _lastInitClientId
  useEffect(() => {
    initializeCoach();
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-initialize when viewingClientId changes (e.g., coach switches to different client)
  useEffect(() => {
    // initialize() will check if client actually changed and skip if not needed
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingClientId]);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <FitTrackHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {isLoadingWorkouts ? (
          <LoadingState />
        ) : viewMode === 'list' ? (
          <WorkoutList />
        ) : (
          <WorkoutCalendar />
        )}

        {/* Coach Notes Section */}
        <CoachNotesSection />
      </main>

      {/* Modals */}
      <AddWorkoutModal isOpen={isAddWorkoutModalOpen} onClose={closeAddWorkoutModal} />
      <ScheduleWorkoutModal isOpen={isScheduleWorkoutModalOpen} onClose={closeScheduleWorkoutModal} />
      <ConnectSheetsModal isOpen={isConnectSheetsModalOpen} onClose={closeConnectSheetsModal} />
      <SchemaMapperModal isOpen={isSchemaMapperOpen} onClose={closeSchemaMapper} />
      <DayDetailModal />
      <ExerciseSearchModal />
      <LiveWorkoutModal isOpen={isLiveWorkoutModalOpen} onClose={closeLiveWorkoutModal} />
      <AddNoteModal isOpen={isAddNoteModalOpen} onClose={closeAddNoteModal} />
    </div>
  );
};

const LoadingState = () => (
  <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-400">Yükleniyor...</p>
    </div>
  </div>
);

export default FitTrackPage;
